import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { MessageCircle, X, Send, ImagePlus, Loader2 } from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const POLL_MS = 2000;

// Routes where the FAB should stay hidden because their primary CTAs
// (lottery submit, saved-slip replay, bet ticket submit) sit in the same
// bottom-right corner.
const HIDDEN_ROUTES = ['/lottery'];

/**
 * Floating support chat FAB + panel rendered on every player page.
 * Only mounts when the logged-in user has role === "user" (we trust the prop).
 * Hides on routes where it would overlap critical action buttons.
 */
const SupportChatFab = () => {
    const { pathname } = useLocation();
    const hidden = HIDDEN_ROUTES.some(p => pathname === p || pathname.startsWith(`${p}/`));
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [pendingImage, setPendingImage] = useState(null);   // data URL
    const [sending, setSending] = useState(false);
    const [unread, setUnread] = useState(0);
    const [connected, setConnected] = useState(true);
    const scrollRef = useRef(null);
    const fileRef = useRef(null);

    const scrollToBottom = () => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    };

    const loadThread = useCallback(async (silent = false) => {
        try {
            const { data } = await axios.get(`${API}/support/my-conversation`);
            setMessages(data.messages || []);
            setConnected(true);
        } catch (e) {
            if (!silent) setConnected(false);
        }
    }, []);

    const loadUnread = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/support/unread-count`);
            setUnread(data.count || 0);
        } catch (e) { /* ignore */ }
    }, []);

    // Background unread poll (runs always, even when panel closed)
    useEffect(() => {
        loadUnread();
        const id = setInterval(loadUnread, POLL_MS * 3);   // every 6s when closed
        return () => clearInterval(id);
    }, [loadUnread]);

    // Listen for "open-support-chat" event so the Header → Messages menu item
    // can pop the panel without the user hunting for a floating button.
    useEffect(() => {
        const handler = () => setOpen(true);
        window.addEventListener('open-support-chat', handler);
        return () => window.removeEventListener('open-support-chat', handler);
    }, []);

    // When open: poll the thread every 2s + mark read on open
    useEffect(() => {
        if (!open) return undefined;
        loadThread(true);
        const id = setInterval(() => loadThread(true), POLL_MS);
        // mark as read on open
        axios.post(`${API}/support/mark-read`).then(() => setUnread(0)).catch(() => {});
        return () => clearInterval(id);
    }, [open, loadThread]);

    useEffect(() => { if (open) scrollToBottom(); }, [messages, open]);

    const pickImage = () => fileRef.current && fileRef.current.click();

    const onFile = (e) => {
        const f = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!f) return;
        if (f.size > MAX_IMAGE_BYTES) { toast.error(`Image too big (${(f.size / 1024 / 1024).toFixed(1)}MB). Max 2MB.`); return; }
        const reader = new FileReader();
        reader.onload = () => setPendingImage(reader.result);
        reader.readAsDataURL(f);
    };

    const send = async () => {
        const body = text.trim();
        if (!body && !pendingImage) return;
        setSending(true);
        try {
            const { data } = await axios.post(`${API}/support/message`, {
                text: body || null,
                image_data_url: pendingImage,
            });
            setMessages(prev => [...prev, data]);
            setText('');
            setPendingImage(null);
            setTimeout(scrollToBottom, 50);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to send');
        } finally {
            setSending(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
    };

    if (hidden) return null;

    return (
        <>
            {/* FAB removed — Messages now lives in the Header dropdown menu, which
                dispatches "open-support-chat" to pop the panel below. */}

            {/* Panel */}
            {open && (
                <div
                    className="fixed bottom-4 right-4 z-[60] w-[340px] max-w-[calc(100vw-32px)] h-[520px] max-h-[calc(100vh-32px)] rounded-2xl overflow-hidden flex flex-col shadow-2xl"
                    style={{
                        background: '#0a0a0a',
                        border: '1px solid rgba(255,215,0,0.3)',
                        boxShadow: '0 12px 40px rgba(0,0,0,0.6), 0 0 24px rgba(255,215,0,0.15)',
                    }}
                    data-testid="support-chat-panel"
                >
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3"
                         style={{ background: 'linear-gradient(90deg, rgba(255,215,0,0.15) 0%, rgba(255,140,0,0.08) 100%)', borderBottom: '1px solid rgba(255,215,0,0.2)' }}>
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-[#39FF14] animate-pulse" />
                            <div>
                                <h3 className="text-sm font-black text-white">Live Support</h3>
                                <p className="text-[9px] text-[#A0AAB2] -mt-0.5">
                                    {connected ? 'We usually reply within minutes' : 'Reconnecting…'}
                                </p>
                            </div>
                        </div>
                        <button onClick={() => setOpen(false)}
                                className="w-7 h-7 rounded-full hover:bg-white/10 flex items-center justify-center"
                                data-testid="support-chat-close">
                            <X className="w-4 h-4 text-white" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div ref={scrollRef}
                         className="flex-1 overflow-y-auto p-3 space-y-2"
                         style={{ background: 'radial-gradient(circle at 50% 0%, rgba(255,215,0,0.03) 0%, transparent 50%)' }}
                         data-testid="support-chat-messages">
                        {messages.length === 0 ? (
                            <div className="text-center py-10 px-4">
                                <MessageCircle className="w-10 h-10 mx-auto mb-2 text-[#FFD700]/30" />
                                <p className="text-[12px] text-[#A0AAB2]">
                                    Got a question? Send us a message and an agent will reply here.
                                </p>
                            </div>
                        ) : (
                            messages.map(m => (
                                <MessageBubble key={m.id} message={m} />
                            ))
                        )}
                    </div>

                    {/* Image preview */}
                    {pendingImage && (
                        <div className="px-3 pt-2 flex items-center gap-2 border-t border-white/5">
                            <img src={pendingImage} alt="attachment" className="h-12 w-12 object-cover rounded border border-white/10" />
                            <span className="text-[10px] text-[#A0AAB2] flex-1">Image ready to send</span>
                            <button onClick={() => setPendingImage(null)}
                                    className="text-[10px] text-[#FF3B30] font-bold">Remove</button>
                        </div>
                    )}

                    {/* Composer */}
                    <div className="flex items-end gap-1.5 p-2.5 border-t border-white/5 bg-[#121212]">
                        <input ref={fileRef} type="file" accept="image/*" onChange={onFile} className="hidden"
                               data-testid="support-chat-file" />
                        <button onClick={pickImage} disabled={sending}
                                className="w-9 h-9 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-[#A0AAB2]"
                                title="Attach image" data-testid="support-chat-attach">
                            <ImagePlus className="w-4 h-4" />
                        </button>
                        <textarea
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            onKeyDown={onKeyDown}
                            placeholder="Type a message…"
                            rows={1}
                            className="flex-1 resize-none bg-[#0a0a0a] border border-white/10 rounded-xl px-3 py-2 text-[12px] text-white placeholder:text-[#555] focus:outline-none focus:border-[#FFD700]/50 max-h-24"
                            data-testid="support-chat-input"
                        />
                        <Button
                            onClick={send}
                            disabled={sending || (!text.trim() && !pendingImage)}
                            size="sm"
                            className="h-9 px-3 rounded-full bg-gradient-to-r from-[#FFD700] to-[#FF8C00] text-black font-black disabled:opacity-40"
                            data-testid="support-chat-send"
                        >
                            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        </Button>
                    </div>
                </div>
            )}
        </>
    );
};

const MessageBubble = ({ message }) => {
    const mine = message.sender_role === 'user';
    const ts = (() => {
        try {
            return new Date(message.created_at).toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' });
        } catch { return ''; }
    })();
    return (
        <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
             data-testid={`support-msg-${message.id}`}>
            <div
                className="max-w-[85%] rounded-xl px-3 py-2"
                style={{
                    background: mine ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.05)',
                    border: mine ? '1px solid rgba(255,215,0,0.3)' : '1px solid rgba(255,255,255,0.08)',
                }}
            >
                {!mine && (
                    <p className="text-[9px] font-bold text-[#FFD700] mb-0.5 uppercase tracking-wider">
                        {message.sender_name || 'Support'}
                    </p>
                )}
                {message.image_data_url && (
                    <img
                        src={message.image_data_url}
                        alt="attachment"
                        className="max-w-full rounded mb-1 border border-white/10"
                        style={{ maxHeight: 180 }}
                    />
                )}
                {message.text && (
                    <p className="text-[12px] text-white whitespace-pre-wrap leading-snug break-words">
                        {message.text}
                    </p>
                )}
                <p className="text-[9px] text-[#666] mt-1 text-right">{ts}</p>
            </div>
        </div>
    );
};

export default SupportChatFab;
